/**
 * Modes de comparaison pour le scoring benchmark.
 *
 * Source unique de vérité côté frontend pour les dropdowns d'édition de
 * schéma. Doit rester aligné avec `backend/app/services/benchmark_service.py`
 * (fonction `compare_values`). Si tu ajoutes/retires un mode côté backend,
 * met à jour cette liste ET le type `FieldCompareMode` dans `types/schema.ts`.
 *
 * Bug historique fixé 2026-05-28 : le dropdown du schema editor inline
 * (FieldEditor) ne contenait que 6 modes — `normalized` et `fuzzy` manquaient.
 * Résultat : pour un champ stocké en BD avec `compare_mode: "normalized"`,
 * le `<select>` ne trouvait pas l'option et affichait la première par défaut
 * (`"exact"`) — risque silencieux d'écraser le tuning empirique PROD1.7.8
 * (24 champs en `normalized`) à la prochaine sauvegarde du schéma.
 */
import type { FieldCompareMode } from '../types/schema';

export interface CompareModeOption {
  value: FieldCompareMode;
  label: string;
  /** Tooltip court — utilisable pour expliquer le mode à l'analyste. */
  description: string;
}

export const COMPARE_MODES: CompareModeOption[] = [
  {
    value: 'exact',
    label: 'Exact (case-insensitive)',
    description: 'Comparaison stricte après lowercase + trim. Différences d\'accents, ponctuation, espaces multiples → mismatch.',
  },
  {
    value: 'normalized',
    label: 'Normalisé (accents/ponctuation/casse)',
    description: 'Retire accents + ponctuation (incl. toutes apostrophes Unicode) + lowercase + collapse espaces. Recommandé pour les champs texte libres.',
  },
  {
    value: 'fuzzy',
    label: 'Flou (similarité ≥ 85%)',
    description: 'Comparaison par similarité Ratcliff-Obershelp ≥ 0.85. Tolère des petites différences typographiques.',
  },
  {
    value: 'contains',
    label: 'Contient',
    description: 'Match si la valeur attendue est contenue dans la valeur extraite (case-insensitive).',
  },
  {
    value: 'numeric_tolerance',
    label: 'Numérique (tolérance)',
    description: 'Match numérique avec tolérance en %. 0% = comparaison exacte.',
  },
  {
    value: 'date',
    label: 'Date',
    description: 'Parse les deux valeurs comme dates et compare au jour près.',
  },
  {
    value: 'list',
    label: 'Liste (set-wise, ordre indifférent)',
    description: 'Compare des valeurs liste/CSV/JSON comme des ensembles. Ordre et casse indifférents.',
  },
  {
    value: 'none',
    label: 'Aucun (exclu du scoring)',
    description: 'Le champ est ignoré dans le calcul du F1.',
  },
];
